import { useSuspenseQuery } from '@apollo/client';
import { ErrorBoundary } from '@sentry/react';
import React, { Suspense } from 'react';
import { useParams } from 'react-router-dom';

import { TableSkeleton } from '@eluve/blocks';
import { Stack, Text } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { TenantOrUserFeatureFlaggedComponent } from '@eluve/smart-blocks';

import { PatientDetailsImportedNotes } from '../PatientDetailsImportedNotes';
import {
  PatientDocuments,
  patientDocumentsFragment,
} from '../PatientDocuments';

const getPatientDocumentsQuery = graphql(
  `
    query getPatientDocuments($tenantId: uuid!, $patientId: uuid!) {
      patientsByPk(tenantId: $tenantId, id: $patientId) {
        __typename
        id
        ...PatientDocuments
      }
    }
  `,
  [patientDocumentsFragment],
);

const PatientDocumentsSection: React.FC = () => {
  const { tenantId, patientId } = useParams() as {
    tenantId: string;
    patientId: string;
  };

  useSuspenseQuery(getPatientDocumentsQuery, {
    variables: { tenantId, patientId },
  });

  return (
    <PatientDocuments patientId={patientId} tenantId={tenantId} hideHeader />
  );
};

export const PatientDocumentsTab: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Stack gap={8}>
      <TenantOrUserFeatureFlaggedComponent flag="INSIGHTS_CHAT">
        <Stack gap={3}>
          <Text size="md" weight="semibold">
            {t('patients.tabs.documents')}
          </Text>
          <Suspense fallback={<TableSkeleton rowCount={5} />}>
            <ErrorBoundary
              fallback={
                <Text>{t('patients.profile.documentsLoadFailed')}</Text>
              }
            >
              <PatientDocumentsSection />
            </ErrorBoundary>
          </Suspense>
        </Stack>
      </TenantOrUserFeatureFlaggedComponent>
      <Stack gap={3}>
        <Text size="md" weight="semibold">
          {t('patients.profile.importedNotesHeading')}
        </Text>
        <ErrorBoundary
          fallback={
            <Text>{t('patients.profile.importedNotesLoadFailed')}</Text>
          }
        >
          <PatientDetailsImportedNotes />
        </ErrorBoundary>
      </Stack>
    </Stack>
  );
};
